/**
 * Shared Motion animation variants, spring configs, and a11y helpers.
 *
 * WCAG 2.3.3 — motion is disabled for users with OS-level "reduce motion"
 * via `useMotionVariants` / `useMotionTransition` (they strip transition
 * timing so final states still settle, just instantly).
 *
 * Usage:
 *   import { fadeIn, useMotionVariants } from "@/lib/animations";
 *   const variants = useMotionVariants(fadeIn);
 *   <m.div variants={variants} initial="hidden" animate="visible" />
 */
import { useMemo } from "react";
import type { Variants, Transition } from "framer-motion";
import { useReducedMotion } from "framer-motion";

// ---------------------------------------------------------------------------
// Spring presets
// ---------------------------------------------------------------------------

/** Smooth, natural spring — good for most UI transitions */
export const springSmooth: Transition = {
  type: "spring",
  stiffness: 300,
  damping: 30,
};

/** Bouncy spring — good for celebratory / attention moments */
export const springBouncy: Transition = {
  type: "spring",
  stiffness: 400,
  damping: 15,
};

/** Stiff, quick spring — good for snappy micro-interactions */
export const springStiff: Transition = {
  type: "spring",
  stiffness: 500,
  damping: 35,
};

/** Duration-based ease for cross-fades */
export const easeFade: Transition = {
  duration: 0.2,
  ease: "easeInOut",
};

/** Reduced-motion no-op transition — settles instantly. */
export const noMotionTransition: Transition = { duration: 0 };

// ---------------------------------------------------------------------------
// Variants
// ---------------------------------------------------------------------------

/** Simple opacity fade */
export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: easeFade },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

/** Slide up + fade (cards, list items) */
export const slideUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: springSmooth,
  },
  exit: { opacity: 0, y: -12, transition: { duration: 0.15 } },
};

/** Slide in from right (panels, drawers) */
export const slideInRight: Variants = {
  hidden: { opacity: 0, x: 80 },
  visible: {
    opacity: 1,
    x: 0,
    transition: springSmooth,
  },
  exit: { opacity: 0, x: 80, transition: { duration: 0.2 } },
};

/** Slide in from left */
export const slideInLeft: Variants = {
  hidden: { opacity: 0, x: -80 },
  visible: {
    opacity: 1,
    x: 0,
    transition: springSmooth,
  },
  exit: { opacity: 0, x: -80, transition: { duration: 0.2 } },
};

/** Scale up from center (modals, popovers) */
export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: springSmooth,
  },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.15 } },
};

/** Scale bounce — attention-grabbing entrance */
export const scaleBounce: Variants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: springBouncy,
  },
};

// ---------------------------------------------------------------------------
// Stagger containers
// ---------------------------------------------------------------------------

/** Parent container — children stagger with 50ms delay */
export const staggerContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.05,
    },
  },
};

/** Fast stagger (30ms) for dense lists */
export const staggerFast: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.03,
      delayChildren: 0.02,
    },
  },
};

/** Slow stagger (80ms) for hero sections */
export const staggerSlow: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

/** Generic list item — use as child of stagger containers */
export const listItem: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: springSmooth,
  },
  exit: { opacity: 0, x: -40, transition: { duration: 0.2 } },
};

// ---------------------------------------------------------------------------
// Page transitions
// ---------------------------------------------------------------------------

/** Cross-fade with subtle scale for route changes */
export const pageTransition: Variants = {
  initial: { opacity: 0, scale: 0.98 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.25, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    scale: 0.98,
    transition: { duration: 0.15, ease: "easeIn" },
  },
};

// ---------------------------------------------------------------------------
// Hover / tap presets (use with m.div whileHover / whileTap)
// ---------------------------------------------------------------------------

/** Lift card on hover with shadow */
export const hoverLift = {
  y: -4,
  scale: 1.01,
  boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
  transition: springSmooth,
};

/** Subtle scale on hover */
export const hoverScale = {
  scale: 1.03,
  transition: springStiff,
};

/** Press-down feedback on tap/click */
export const tapShrink = {
  scale: 0.97,
};

// ---------------------------------------------------------------------------
// Reduced-motion helpers (WCAG 2.3.3)
// ---------------------------------------------------------------------------

/**
 * Walks a Variants object and replaces every `transition` with `{ duration: 0 }`,
 * preserving the destination values so the layout settles — it just settles
 * instantly.
 */
function stripVariantMotion(variants: Variants): Variants {
  const out: Variants = {};
  for (const key of Object.keys(variants)) {
    const v = variants[key];
    if (typeof v === "function") {
      out[key] = (...args: unknown[]) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const resolved = (v as (...a: any[]) => any)(...args);
        if (resolved && typeof resolved === "object") {
          return { ...resolved, transition: noMotionTransition };
        }
        return resolved;
      };
      continue;
    }
    if (v && typeof v === "object") {
      out[key] = { ...v, transition: noMotionTransition };
    } else {
      out[key] = v;
    }
  }
  return out;
}

/**
 * Hook: returns variants with motion stripped when the OS-level
 * "prefers-reduced-motion" setting is enabled. Otherwise the original
 * variants come back untouched.
 */
export function useMotionVariants<T extends Variants>(variants: T): T {
  const reduced = useReducedMotion();
  return useMemo(
    () => (reduced ? (stripVariantMotion(variants) as T) : variants),
    [reduced, variants],
  );
}

/**
 * Hook: collapse a transition to zero-duration when reduced-motion is on.
 * Use with `whileHover` / `whileTap` preset transitions.
 */
export function useMotionTransition(transition: Transition): Transition {
  const reduced = useReducedMotion();
  return reduced ? noMotionTransition : transition;
}

export { useReducedMotion };

// ---------------------------------------------------------------------------
// Utility: shimmer gradient class for skeleton loading
// ---------------------------------------------------------------------------

/**
 * CSS class name for shimmer skeleton loading.
 * Add to tailwind config:
 *   @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
 *   .animate-shimmer { animation: shimmer 1.5s infinite linear; background-size: 200% 100%; }
 */
export const SHIMMER_CLASS =
  "bg-gradient-to-r from-surface-secondary via-surface/80 to-surface-secondary animate-shimmer rounded";

// ---------------------------------------------------------------------------
// Design Refresh v1 — Signature "WOW" motion variants (2026-04-20)
//
// Named in the Design Refresh plan at docs/DESIGN_REFRESH_PLAN.md. Every
// new UI surface in Phase 2+ uses these so the motion grammar is coherent.
// All variants are ``useMotionVariants``-safe — reduced-motion users get
// static transitions.
//
// Pair with the CSS custom properties:
//   --ease-wow, --ease-spring, --ease-enter, --ease-exit
//   --duration-fast / --duration-base / --duration-slow / --duration-reveal
//
// motion.<x> whileInView / animate usage pattern:
//   <m.div variants={useMotionVariants(revealFromBelow)}
//          initial="hidden" whileInView="visible"
//          viewport={{ once: true, margin: "-40px" }}/>
// ---------------------------------------------------------------------------

const EASE_WOW: [number, number, number, number] = [0.2, 0.8, 0.2, 1];
const EASE_SPRING: [number, number, number, number] = [0.34, 1.56, 0.64, 1];

/** Element emerges from below with a 20px lift — default for page sections. */
export const revealFromBelow: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.36, ease: EASE_WOW },
  },
};

/** Stagger grid / list children with a 40ms gap — the Modernize dashboard feel. */
export const staggerCards: Variants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.04, delayChildren: 0.05 },
  },
};

/** Child of staggerCards — reveal + scale settle. */
export const staggerCardItem: Variants = {
  hidden: { opacity: 0, y: 12, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.32, ease: EASE_WOW },
  },
};

/** Chat message arrival — subtle lift + scale settle. */
export const messageReveal: Variants = {
  hidden: { opacity: 0, y: 8, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.32, ease: EASE_WOW },
  },
};

/** Dialog / popover entrance — scale-in with fade + backdrop blur gate. */
export const dialogEnter: Variants = {
  hidden: { opacity: 0, scale: 0.96 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.24, ease: EASE_WOW },
  },
  exit: {
    opacity: 0,
    scale: 0.97,
    transition: { duration: 0.18, ease: "easeIn" },
  },
};

/** Side-panel slide — used for detail drawers, sidebars, activity indicator. */
export const sidebarSlide: Variants = {
  hidden: { opacity: 0, x: 24 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.28, ease: EASE_WOW },
  },
  exit: {
    opacity: 0,
    x: 16,
    transition: { duration: 0.2, ease: "easeIn" },
  },
};

/** Hover signature — lift + glow. Use with whileHover. */
export const hoverGlow = {
  y: -2,
  boxShadow:
    "0 6px 12px -2px rgba(15, 23, 42, 0.08), 0 12px 32px -8px rgba(59, 122, 255, 0.16)",
  transition: { duration: 0.24, ease: EASE_WOW },
};

/** Pulse for in-progress status pills — pair with Infinity repeat. */
export const statusPulse: Variants = {
  idle: { opacity: 1 },
  pulse: {
    opacity: [1, 0.6, 1],
    transition: { duration: 2.4, repeat: Infinity, ease: "easeInOut" },
  },
};

/** Spring-in entrance — for hero stats, toast pops, cancel confirmations. */
export const springIn: Variants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: EASE_SPRING },
  },
};

/** Parallax drift for hero backgrounds — slow Y translate as mouse moves.
 *  Apply via motionValue binding in a component; this is just the timing.  */
export const parallaxTiming = {
  duration: 0.6,
  ease: EASE_WOW,
};
